import { useCallback, useEffect, useRef, useState } from 'react';
import type { RectMm } from '../layout/doorLayout';
import {
  PREVIEW_FALLBACK_VIEWPORT_ASPECT,
  clampPreviewScale,
  focusForZoomToward,
  isFitScale,
  remapFocusToWorld,
  toggleClickScale,
  viewBoxForCamera,
  worldCentre,
} from './previewZoom';

export function usePreviewZoom(
  world: RectMm,
  enabled: boolean,
  viewportAspect = PREVIEW_FALLBACK_VIEWPORT_ASPECT
) {
  const [scale, setScale] = useState(1);
  const [focus, setFocus] = useState(() => worldCentre(world));
  const worldRef = useRef(world);
  const aspectRef = useRef(viewportAspect);
  const scaleRef = useRef(scale);
  const focusRef = useRef(focus);
  scaleRef.current = scale;
  focusRef.current = focus;
  aspectRef.current = viewportAspect;

  useEffect(() => {
    const previous = worldRef.current;
    worldRef.current = world;
    if (
      previous.x === world.x &&
      previous.y === world.y &&
      previous.width === world.width &&
      previous.height === world.height
    ) {
      return;
    }
    setFocus((current) => remapFocusToWorld(previous, world, current));
  }, [world]);

  useEffect(() => {
    if (enabled) return;
    setScale(1);
    setFocus(worldCentre(worldRef.current));
  }, [enabled]);

  const viewBox = viewBoxForCamera(
    world,
    enabled ? scale : 1,
    focus.x,
    focus.y,
    viewportAspect
  );
  const zoomed = enabled && !isFitScale(scale);

  const reset = useCallback(() => {
    setScale(1);
    setFocus(worldCentre(worldRef.current));
  }, []);

  const zoomToward = useCallback((nextScale: number, anchor: { x: number; y: number }) => {
    const currentWorld = worldRef.current;
    const aspect = aspectRef.current;
    const currentBox = viewBoxForCamera(
      currentWorld,
      scaleRef.current,
      focusRef.current.x,
      focusRef.current.y,
      aspect
    );
    const clamped = clampPreviewScale(nextScale);
    const nextFocus = focusForZoomToward(currentWorld, currentBox, clamped, anchor, aspect);
    scaleRef.current = clamped;
    focusRef.current = nextFocus;
    setScale(clamped);
    setFocus(nextFocus);
  }, []);

  const toggleAt = useCallback((anchor: { x: number; y: number }) => {
    zoomToward(toggleClickScale(scaleRef.current), anchor);
  }, [zoomToward]);

  const panBy = useCallback((dx: number, dy: number) => {
    if (isFitScale(scaleRef.current)) return;
    const next = { x: focusRef.current.x - dx, y: focusRef.current.y - dy };
    focusRef.current = next;
    setFocus(next);
  }, []);

  return { viewBox, zoomed, scale, reset, zoomToward, toggleAt, panBy };
}
